import { useState, useEffect, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CalendarConnection, CalendarProvider } from "@/types/calendar";
import { useCalendarConnections } from "@/hooks/useCalendarConnections";
import CalendarIntegrationHeader from "@/components/calendar/CalendarIntegrationHeader";
import CalendarConnectionsList from "@/components/calendar/CalendarConnectionsList";
import CalendarIntegrationContent from "@/components/calendar/CalendarIntegrationContent";
import CalendarProviderDialog from "@/components/calendar/CalendarProviderDialog";
import CalendarDisconnectDialog from "@/components/calendar/CalendarDisconnectDialog";

const CalendarIntegration = () => {
  const { toast } = useToast();
  const { currentUser, session } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<CalendarProvider | null>(null);
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<CalendarConnection | null>(null);
  const [autoExpandSettings, setAutoExpandSettings] = useState(false);
  const [shouldRedirectToHome, setShouldRedirectToHome] = useState(false);
  const calendarSectionRef = useRef<HTMLDivElement>(null);

  const { 
    calendarConnections, 
    isCheckingConnection, 
    handleCalendarConnectionUpdate, 
    removeCalendarConnection 
  } = useCalendarConnections();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldSelectCalendars = urlParams.get('selectCalendars') === 'true';
    const isFirstTime = urlParams.get('firstTime') === 'true';
    const isSetup = urlParams.get('setup') === 'true';
    
    if (shouldSelectCalendars) {
      setAutoExpandSettings(true);
      setShouldRedirectToHome(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Show welcome message for first-time users
    if (isFirstTime) {
      toast({
        title: "Welcome to Shared Matter! 🎉",
        description: "To get the most out of Shared Matter, connect your calendars below.",
        duration: 6000,
      });
      // Clean up the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Show setup message for users with no calendars
    if (isSetup) {
      toast({
        title: "🗓️ Connect Your Calendars",
        description: "I can help you stay organized and never miss important events when your calendars are connected!",
        duration: 6000,
      });
      // Clean up the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  useEffect(() => {
    const shouldScrollToSelection = autoExpandSettings && calendarConnections.length > 0;
    if (shouldScrollToSelection && calendarSectionRef.current) {
      setTimeout(() => {
        calendarSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [calendarConnections.length, autoExpandSettings]);

  const handleShowProviderSelection = () => {
    if (!currentUser) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to connect your calendar",
      });
      return;
    }
    
    setShowDialog(true);
  };

  const handleConnectCalendar = async (provider: CalendarProvider) => {
    if (!currentUser || !session) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to connect your calendar",
      });
      return;
    }

    setSelectedProvider(provider);
    setIsConnecting(true);
    setShowDialog(false);
    
    try {
      const redirectUri = `${window.location.origin}/calendar/callback`;
      console.log("Using redirect URI:", redirectUri);
      
      const oauthEndpoint = provider === 'google' 
        ? 'get-google-calendar-oauth-url'
        : 'get-outlook-calendar-oauth-url';
      
      const response = await fetch(
        `https://erticlijceutpetbahff.supabase.co/functions/v1/${oauthEndpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            userId: currentUser.id,
            redirectUri: redirectUri
          }),
        }
      );

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (data.url) {
        sessionStorage.setItem('calendarReturnUrl', window.location.pathname);
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error connecting to calendar:", error);
      toast({
        variant: "destructive",
        title: "Connection Failed",
        description: error instanceof Error ? error.message : `Failed to connect to ${provider === 'google' ? 'Google' : 'Outlook'} Calendar`,
      });
      setIsConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!currentUser || !connectionToDisconnect) return;
    
    try {
      const { error } = await supabase
        .from('calendar_connections')
        .delete()
        .eq('id', connectionToDisconnect.id)
        .eq('user_id', currentUser.id);
        
      if (error) {
        throw error;
      }
      
      removeCalendarConnection(connectionToDisconnect.id);
      
      setShowDisconnectDialog(false);
      setConnectionToDisconnect(null);
      
      const accountInfo = connectionToDisconnect.account_email ? ` (${connectionToDisconnect.account_email})` : '';
      toast({
        title: `${getProviderDisplayName(connectionToDisconnect.provider)} Disconnected`,
        description: `Your ${getProviderDisplayName(connectionToDisconnect.provider)}${accountInfo} has been disconnected successfully.`,
      });
    } catch (error) {
      console.error("Error disconnecting calendar:", error);
      toast({
        variant: "destructive",
        title: "Disconnection Failed",
        description: `Failed to disconnect your ${getProviderDisplayName(connectionToDisconnect.provider)}. Please try again.`,
      });
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Google Calendar';
      case 'outlook':
        return 'Outlook Calendar';
      default:
        return 'Calendar';
    }
  };

  const availableProviders: CalendarProvider[] = ['google', 'outlook'];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-primary/20 via-accent/10 to-secondary overflow-hidden">
      <CalendarIntegrationHeader />
      
      <div className="container mx-auto max-w-4xl pb-6 sm:pb-12 -mt-6 sm:-mt-12 px-4">
        <div className="bg-card border border-border p-4 sm:p-8 rounded-2xl shadow-xl">
          <div className="flex flex-col items-center gap-4">
            <CalendarConnectionsList
              ref={calendarSectionRef}
              connections={calendarConnections}
              onUpdated={handleCalendarConnectionUpdate}
              onDisconnect={(conn) => {
                setConnectionToDisconnect(conn);
                setShowDisconnectDialog(true);
              }}
              autoExpandSettings={autoExpandSettings}
              shouldRedirectToHome={shouldRedirectToHome}
            />
            
            <CalendarIntegrationContent
              isCheckingConnection={isCheckingConnection}
              availableProviders={availableProviders}
              isConnecting={isConnecting}
              currentUser={currentUser}
              onShowProviderSelection={handleShowProviderSelection}
            />
          </div>
        </div>
      </div>

      <CalendarProviderDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        availableProviders={availableProviders}
        onConnectCalendar={handleConnectCalendar}
      />

      <CalendarDisconnectDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
        connection={connectionToDisconnect}
        onConfirmDisconnect={handleDisconnectCalendar}
      />
    </div>
  );
};

export default CalendarIntegration;
